import type { ValidEndpoints } from '@/api/clients/backend-client'
import type { CrudConverter } from './converter-helpers'
import { createItem, deleteItem, fetchCollection, updateItem, type ApiResult } from './helper-functions'

const collectionName: ValidEndpoints = 'asset_bookings'

type Asset_Bookings_Expected_API_DTO = {
  id: string
  // fleet_id: string
  start: string // timestamp
  end: string // timestamp
  asset_type: string
  asset_id: string
  booked_user_id: string
}

const process_asset_type = (api: Asset_Bookings_Expected_API_DTO): 'vehicle' | 'charger' => {
  if (api.asset_type === 'vehicle' || api.asset_type === 'charger') return api.asset_type
  // TODO: this is weak but only included to support legacy
  return 'vehicle'
}

const assetBookingsConverter: CrudConverter<
  Asset_Bookings_Expected_API_DTO,
  DirectusAssetBooking,
  DirectusAssetBookingCreationData,
  Partial<Omit<DirectusAssetBooking, 'id'>>
> = {
  apiToLegacy(api) {
    return {
      asset_id: api.asset_id,
      asset_type: process_asset_type(api),
      end: api.end,
      id: api.id,
      start: api.start,
      user_id: api.booked_user_id,
    }
  },
  legacyToApi(legacy) {
    return {
      asset_id: legacy.asset_id,
      asset_type: legacy.asset_type,
      booked_user_id: legacy.user_id,
      end: legacy.end,
      start: legacy.start,
      id: legacy.id,
    }
  },
  createToApi(create) {
    return {
      asset_id: create.asset_id,
      asset_type: create.asset_type,
      booked_user_id: create.user_id,
      start: create.start,
      end: create.end,
    }
  },
  updateToApi(update) {
    const collectorOjb: Partial<Asset_Bookings_Expected_API_DTO> = {}

    if (update.user_id) collectorOjb.booked_user_id
    if (update.asset_id) collectorOjb.asset_id
    if (update.asset_type) collectorOjb.asset_type
    if (update.start) collectorOjb.start
    if (update.end) collectorOjb.end

    return collectorOjb
  },
}

/**
 * Fetches all bookings for a given asset from directus.
 *
 * @param assetID the id of the asset or list of assets you need to fetch the bookings for
 * @returns a list of `DirectusAssetBooking` objects if successful undefined if it errors out.
 */
export async function fetchAssetBookingsByAssetID(
  assetID: string | number | string[] | number[],
): Promise<DirectusAssetBooking[] | undefined> {
  return await fetchCollection<Asset_Bookings_Expected_API_DTO, DirectusAssetBooking>(
    collectionName,
    {
      asset_id: assetID,
    },
    assetBookingsConverter.apiToLegacy,
  )
}

/**
 * Fetches all bookings for a given user from directus.
 *
 * @param userID the id of the user you need to fetch the bookings for
 * @returns a list of `DirectusAssetBooking` objects if successful undefined if it errors out.
 */
export async function fetchAssetBookingsByUserID(
  userID: string | number,
): Promise<DirectusAssetBooking[] | undefined> {
  return await fetchCollection<Asset_Bookings_Expected_API_DTO, DirectusAssetBooking>(
    collectionName,
    {
      booked_user_id: userID,
    },
    assetBookingsConverter.apiToLegacy,
  )
}

/**
 * Creates an asset booking record in directus.
 *
 * @param bookingData data for the booking to be created
 * @returns the created booking
 */
export async function createAssetBooking(
  bookingData: DirectusAssetBookingCreationData,
): Promise<ApiResult<DirectusAssetBooking>> {
  return await createItem<Asset_Bookings_Expected_API_DTO, DirectusAssetBooking>(
    collectionName,
    assetBookingsConverter.createToApi(bookingData),
    assetBookingsConverter.apiToLegacy,
  )
}

/**
 * Deletes an asset booking record from directus.
 *
 * @param bookingId the record id of the booking to be deleted.
 * @returns status of the operations outcome.
 */
export async function deleteAssetBooking(bookingId: string) {
  return await deleteItem(collectionName, bookingId)
}

/**
 * Updates an asset booking record in directus.
 *
 * @param bookingId the record id of the booking to be updated.
 * @param dataToUpdate the booking data to be updated.
 * @returns the updated asset booking record if successful undefined if not.
 */
export async function updateAssetBooking(
  bookingId: string,
  dataToUpdate: Partial<DirectusAssetBooking>,
): Promise<DirectusAssetBooking | undefined> {
  return await updateItem<DirectusAssetBooking>({
    endpoint: collectionName,
    query: { id: bookingId },
    body: assetBookingsConverter.updateToApi(dataToUpdate),
    converter: assetBookingsConverter.apiToLegacy,
  })
}

export interface DirectusAssetBooking {
  /** UUID for this booking. */
  id: string // UUID
  status?: 'Published' | 'Draft' | 'Archived'
  sort?: number
  /** Related UUID for user that created this booking. */
  user_created?: string // related UUID
  /** The DateTime string for when this booking was created. */
  date_created?: string // DateTime
  /** Related UUID for user that updated this booking. */
  user_updated?: string // related UUID
  /** The DateTime string for when this booking was updated. */
  date_updated?: string // DateTime
  /** The DateTime string for when this booking is meant to start. */
  start: string
  /** The DateTime string for when this booking is meant to end. */
  end: string
  /** The type of asset that has been booked */
  asset_type: 'vehicle' | 'charger'
  /**
   * The id for the asset.
   *
   * NOTE: can be an external identifier with no directus relation. Can also be
   * either a stringified int or UUID, the client will need to process this.
   */
  asset_id: string // UUID or stringified int client will have to handle parsing.
  /**
   * Related UUID for user that this booking is for.
   *
   * NOTE: can be different from the user making the booking.
   */
  user_id: string // related UUID
}

interface DirectusAssetBookingCreationData {
  /** The DateTime string for when this booking is meant to start. */
  start: string
  /** The DateTime string for when this booking is meant to end. */
  end: string
  /** The type of asset that has been booked */
  asset_type: 'vehicle' | 'charger'
  /**
   * The id for the asset.
   *
   * NOTE: can be an external identifier with no directus relation. Can also be
   * either a stringified int or UUID, the client will need to process this.
   */
  asset_id: string // UUID or stringified int client will have to handle parsing.
  /**
   * Related UUID for user that this booking is for.
   *
   * NOTE: can be different from the user making the booking.
   */
  user_id: string // related UUID
}
